// Stable links to items in the portal, `/go/<type>/<id>`, made where the item is known by its ID
// alone: event facts, emails, connector messages, Stripe returns. The app looks the item up,
// checks the visitor may open it and redirects to its page wherever the portal keeps it now, so a
// link sent out keeps working when portal addresses change. Inside the portal, links go straight
// to the page (`permalinkPortalPath`).

/** What a permalink can point at. */
export const PERMALINK_TYPES = [
  "asset",
  "project",
  "organisation",
  "policy",
  "api-key",
  "connector",
  "hash-list",
  "lens",
  "upload-preset",
] as const;
export type PermalinkType = (typeof PERMALINK_TYPES)[number];

/** Types whose permalink may name a page of the item after its ID, such as `/go/project/<id>/usage`. */
const TYPES_WITH_PAGES: readonly PermalinkType[] = ["project", "organisation"];
const PAGE_PATTERN = /^(\/[a-z0-9-]+)*$/;

/**
 * The permalink of an item by ID: `permalink("asset", asset.id)`. A project or organisation can add
 * one of its pages (`permalink("organisation", orgId, "/billing")`), and a query to pass on.
 */
export const permalink = (type: PermalinkType, id: string, page = "") =>
  `/go/${type}/${encodeURIComponent(id)}${page}`;

/** The item and page a permalink path (with any query) names, or undefined when it isn't one. */
export const parsePermalink = (
  path: string,
): { type: PermalinkType; id: string; page: string; search: string } | undefined => {
  const [pathname = "", ...query] = path.split("?");
  const [, root, type, id, ...rest] = pathname.split("/");
  if (root !== "go" || !id || !(PERMALINK_TYPES as readonly string[]).includes(type ?? ""))
    return undefined;
  const page = rest.length ? `/${rest.join("/")}` : "";
  if (page && (!TYPES_WITH_PAGES.includes(type as PermalinkType) || !PAGE_PATTERN.test(page)))
    return undefined;
  return {
    type: type as PermalinkType,
    id: decodeURIComponent(id),
    page,
    search: query.length ? `?${query.join("?")}` : "",
  };
};

/** A link for outside the app (a webhook, an email): internal paths become absolute on `origin`. */
export const absoluteAppLink = (path: string, origin: string): string =>
  path.startsWith("/") && !path.startsWith("//") ? new URL(path, origin).href : path;

/**
 * Stand-ins for "the visitor's current project / organisation" in portal addresses
 * (`/portal/proj_current/…`), for links made where only the page is known (docs, menus). The portal
 * resolves them to the scope the visitor has open and redirects to its own address.
 */
export const CURRENT_PROJECT_REF = "proj_current";
export const CURRENT_ORGANISATION_REF = "org_current";

/** A project's page by its ID (`/portal/proj_…/…`), or `CURRENT_PROJECT_REF`. */
export const projectPagePath = (projectRef: string, page = "") =>
  `/portal/${encodeURIComponent(projectRef)}${page}`;
/** An organisation's page by its ID (`/portal/org_…/…`), or `CURRENT_ORGANISATION_REF`. */
export const organisationPagePath = (orgRef: string, page = "") =>
  `/portal/${encodeURIComponent(orgRef)}${page}`;

/**
 * Where a permalink opens inside the portal, without the redirect: an item of a project (an
 * event's asset) is in `projectId`'s pages, or the current project's when it isn't given. Items
 * whose page needs more than their ID (a lens's identifier) keep the permalink. Other paths are
 * unchanged.
 */
export const permalinkPortalPath = (path: string, projectId?: string): string => {
  const link = parsePermalink(path);
  if (!link) return path;
  const id = encodeURIComponent(link.id);
  const inProject = (page: string) =>
    projectPagePath(projectId ?? CURRENT_PROJECT_REF, `${page}${link.search}`);
  switch (link.type) {
    case "asset":
      return inProject(`/assets/${id}`);
    case "hash-list":
      return inProject(`/hash-lists/${id}`);
    case "connector":
      return inProject("/connectors");
    case "api-key":
      return inProject("/api-keys");
    case "upload-preset":
      return inProject("/upload-presets");
    case "project":
      return projectPagePath(link.id, `${link.page}${link.search}`);
    case "organisation":
      return organisationPagePath(link.id, `${link.page}${link.search}`);
    case "policy":
      return organisationPagePath(
        CURRENT_ORGANISATION_REF,
        `/moderation-policies/${id}${link.search}`,
      );
    case "lens":
      return path;
  }
};
